import React, { createContext, ReactNode, useContext, useState } from 'react';
import { Brand } from '@restore/services-lib';
import { ApolloError, useMutation, useQuery } from '@apollo/client';
import { CREATE_BRAND } from 'graphql/brands/mutations/createBrand.gql';
import { GET_BRANDS } from 'graphql/brands/queries/getBrands.gql';
import { UPDATE_BRAND } from 'graphql/brands/mutations/updateBrand.gql';
import { ARCHIVE_BRAND } from 'graphql/brands/mutations/archiveBrand.ql';
import { validateEntityName } from 'const/regex';
import { useAuthContext } from 'context/auth.context';
import { useSnackbarContext } from 'context/snackbar.context';
import { DropdownOption } from 'components/DropdownSelect/dropdown.types';
import { hasPermissions } from 'config/ability';

export interface CreateBrandInterface {
  name: string;
}

export interface UpdateBrandInterface extends CreateBrandInterface {
  id: number;
}

interface BrandsContextInterface {
  brands: Brand[];
  setBrands: (brands: Brand[]) => void;
  addBrand: (brand: CreateBrandInterface) => Promise<void>;
  editBrand: (brand: UpdateBrandInterface) => Promise<void>;
  archiveBrand: (id: number) => Promise<void>;
  addBrandLoading: boolean;
  editBrandLoading: boolean;
  archiveBrandLoading: boolean;
  getBrandById: (id: number) => Brand | undefined;
  getBrandDropdownSelectOptionsByIds: (brandIds: number[]) => DropdownOption[] | undefined;
  loading: boolean;
  error?: ApolloError;
}

const defaultContext: BrandsContextInterface = {
  brands: [],
  setBrands: () => {},
  addBrand: async () => {},
  editBrand: async () => {},
  archiveBrand: async () => {},
  addBrandLoading: false,
  editBrandLoading: false,
  archiveBrandLoading: false,
  getBrandById: () => undefined,
  getBrandDropdownSelectOptionsByIds: () => undefined,
  loading: false,
};

const BrandsContext = createContext<BrandsContextInterface>(defaultContext);

export const BrandsContextProvider = ({ children }: { children: ReactNode }) => {
  const [brands, setBrands] = useState<Brand[]>([]);
  const [createBrand, { loading: addBrandLoading }] = useMutation(CREATE_BRAND);
  const [updateBrand, { loading: editBrandLoading }] = useMutation(UPDATE_BRAND);
  const [archiveBrandQuery, { loading: archiveBrandLoading }] = useMutation(ARCHIVE_BRAND);
  const { ability, userTenantId } = useAuthContext();
  const { displaySnackbarMessage, displayApiResult } = useSnackbarContext();

  // fetch brands
  const { loading, error, refetch } = useQuery(GET_BRANDS, {
    skip: !userTenantId || !hasPermissions(ability, 'read', 'Brand'),
    variables: {
      tenantId: userTenantId,
    },
    onCompleted: ({ brands }: { brands: Brand[] }) => {
      setBrands(brands);
    },
  });

  // this is an EXTRA check. validation is being handled upon name entry. in case that gets accidentally changed, this will prevent submission
  const handleValidateName = (name?: string | null) => {
    return !(!name || name.length < 2 || !validateEntityName(name));
  };

  const addBrand = async (brand: CreateBrandInterface) => {
    if (handleValidateName(brand.name)) {
      await createBrand({
        variables: {
          input: {
            ...brand,
            tenantId: userTenantId,
          },
        },
      }).then(
        (result) => {
          displayApiResult(result.data.createBrand, 'Created successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      await refetch();
    }
  };

  const editBrand = async (input: UpdateBrandInterface) => {
    if (handleValidateName(input.name)) {
      await updateBrand({
        variables: {
          input,
        },
      }).then(
        (result) => {
          displayApiResult(result.data.updateBrand, 'Updated successfully');
        },
        (rejection) => {
          displaySnackbarMessage(rejection.message, 'error');
        }
      );
      await refetch();
    }
  };

  const archiveBrand = async (id: number) => {
    await archiveBrandQuery({
      variables: {
        id: id,
      },
    }).then(
      (result) => {
        displayApiResult(result.data.archiveBrand, 'Archived successfully');
      },
      (rejection) => {
        displaySnackbarMessage(rejection.message, 'error');
      }
    );
    await refetch();
  };

  const getBrandById = (id: number) => {
    return brands.find((brand) => brand.id === id);
  };

  // get brand dropdown select options from an array of store ids
  const getBrandDropdownSelectOptionsByIds = (brandIds: number[]) => {
    const brands = brandIds.map((id) => getBrandById(id));
    const brandsDropdownOptions: DropdownOption[] = [];
    brands.map((brand) => brand && brandsDropdownOptions.push({ name: brand.name, id: brand.id }));
    return brandsDropdownOptions;
  };

  return (
    <BrandsContext.Provider
      value={{
        brands,
        setBrands,
        addBrand,
        editBrand,
        archiveBrand,
        addBrandLoading,
        editBrandLoading,
        archiveBrandLoading,
        getBrandById,
        getBrandDropdownSelectOptionsByIds,
        loading,
        error,
      }}
    >
      {children}
    </BrandsContext.Provider>
  );
};

export const useBrandsContext = () => useContext(BrandsContext);
